<template>
  <DModal :isBgOverlay="true" :show-close="false" @close="onCancel">
    <div class="do-modal do-modal__reasons-form" slot="insertComponent">
      <div class="do-modal__header">
        <h2>{{ i18n.create_absence }}</h2>
        <div class="icon__bg-circle ww-modal__header__close" @click="onCancel">
          <span class="icon-cross2"></span>
        </div>
      </div>
      <div class="do-modal__body">
        <div class="d-group" v-if="isAbsenceIntegrationActive && isSalarySystemKMD">
          <label class="d-label marg-025-bottom">Vælg fraværsårsag fra OPUS</label>
          <DSelect
            :key="'absenceCode-' + resetDSelectCounter"
            ref="absenceCodeSelect"
            :items="absenceCodesForDSelect"
            :multiple="false"
            :placeholder="'Vælg kode fra OPUS'"
            :itemsSelected="selectedAbsenceCodeForDSelect"
            :isShowRemoveButton="false"
            @add="onSelectAbsenceCode"
            @remove="onSelectAbsenceCode(null)"
          />
        </div>
        <div class="d-group" v-if="isAbsenceIntegrationActive && isSalarySystemSD">
          <label class="d-label marg-025-bottom">Vælg fraværsårsag fra SD</label>
          <DSelect
            :key="'sdAbsenceCode-' + resetDSelectCounter"
            ref="sdAbsenceCodeSelect"
            :items="sdAbsenceCodesForDSelect"
            :multiple="false"
            :placeholder="'Vælg kode fra SD'"
            :itemsSelected="selectedSdAbsenceCodeForDSelect"
            :isShowRemoveButton="false"
            @add="onSelectSdAbsenceCode"
            @remove="onSelectSdAbsenceCode(null)"
          />
        </div>
        <div class="d-group marg-1-top">
          <label class="d-label">{{ i18n.title }}</label>
          <input
            class="d-input"
            v-focus
            v-model="newReasonTitle"
            @keyup.enter="onCreate"
            @input="clearErrors"
            :class="{ 'd-input--error': errors.newReasonTitle }"
          >
          <p class="input-error" v-if="errors.newReasonTitle">{{ errors.newReasonTitle }}</p>
        </div>
        <div v-if="shouldShowDefaultOngoing" class="d-label-and-toggle marg-1-top" @click.stop="defaultOngoing = !defaultOngoing">
          <label class="d-label">{{ i18n.default_ongoing }}</label>
          <AppToggle :propOn="defaultOngoing" :allowChanges="false"></AppToggle>
        </div>
        <div class="d-label-and-toggle marg-1-top" @click.stop="canRequest = !canRequest">
          <label class="d-label">{{ i18n.can_be_requested }}</label>
          <AppToggle :propOn="canRequest" :allowChanges="false"></AppToggle>
        </div>
        <div class="d-label-and-toggle marg-1-top" @click.stop="defaultPrivate = !defaultPrivate">
          <label class="d-label">{{ i18n.hide_title }}</label>
          <AppToggle :propOn="defaultPrivate" :allowChanges="false"></AppToggle>
        </div>
        <p class="marg-1-top italic">
          <span v-html="i18n.default_private_explained"></span>
        </p>
      </div>
      <div class="do-modal__footer">
        <button class="do-bttn" @click="onCancel">{{ $t('vue2.common.cancel') }}</button>
        <button
          class="do-bttn do-bttn--submit"
          :class="{ 'do-bttn--submit--loading': isCreating }"
          @click="onCreate"
        >
          {{ $t('vue2.common.create') }}
        </button>
      </div>
    </div>
  </DModal>
</template>

<script>
const I18N = {
  da: {
    absence_reasons: 'Fraværsårsager',
    create_absence: 'Opret fraværsårsag',
    need_a_title: 'Mangler et navn',
    title: 'Titel på fraværsårsag',
    hide_title: 'Skjul fraværstitel',
    archive: 'Arkivér',
    dearchive: 'Genskab',
    no_absence_reasons_created: 'Ingen fraværsårsager oprettet',
    tooltip_for_hide_title: 'Når "Skjul titel" er aktiveret, er fraværstitlen kun synlig for administratorer og brugeren med fraværet – for andre vises blot "Fravær". Hvis indstillingen deaktiveres, er titlen synlig for alle, også i Aula.',
    default_private_explained: 'Hvis "Skjul fraværstitel" er slået til, vil administratorer – samt brugeren med fraværet – se fraværets titel,<br>mens andre brugere blot ser "Fravær" som titel på fraværet.',
    default_ongoing: 'Automatisk løbende fravær',
    default_ongoing_explained: 'Aktivér for automatisk at markere fraværet som løbende, når denne årsag vælges ved oprettelse af fravær',
    can_be_requested: 'Kan anmodes',
    can_be_requested_explained: 'Medarbejdere kan vælge denne årsag, når de anmoder om fravær'
  },
  nb: {
    absence_reasons: 'Fraværsårsaker',
    create_absence: 'Opprett fraværsårsak',
    hide_title: 'Skjul tittel',
    archive: 'Arkiver',
    dearchive: 'Gjenopprett',
    title: 'Tittel',
    need_a_title: 'Mangler et navn',
    no_absence_reasons_created: 'Ingen fraværsårsaker opprettet',
    tooltip_for_hide_title: 'Når "Skjul tittel" er aktivert, er fraværstittelen kun synlig for administratorer og brukeren fraværet gjelder – for andre vises bare "Fravær". Hvis innstillingen deaktiveres, blir tittelen synlig for alle.',
    default_private_explained: 'Hvis "Skjul fraværstittel" er aktivert, vil administratorer – samt brukeren med fraværet – se fraværets tittel,<br>mens andre brukere kun ser "Fravær" som tittel på fraværet.',
    default_ongoing: 'Løpende fravær',
    default_ongoing_explained: 'Aktiver for automatisk å merke fraværet som løpende når denne årsaken velges ved registrering av fravær',
    can_be_requested: 'Kan forespørres',
    can_be_requested_explained: 'Ansatte kan velge denne årsaken når de ber om fravær'
  },
  en: {
    absence_reasons: 'Absence reasons',
    create_absence: 'Create absence reason',
    hide_title: 'Hide title',
    archive: 'Archive',
    dearchive: 'Restore',
    title: 'Title',
    need_a_title: 'Missing a name',
    no_absence_reasons_created: 'No absence reasons created',
    tooltip_for_hide_title: 'When "Hide title" is enabled, the absence title is only visible to administrators and the user the absence relates to – others will simply see "Absence". If the setting is disabled, the title becomes visible to everyone.',
    default_private_explained: 'If "Hide absence title" is enabled, administrators – as well as the user with the absence – will see the absence title,<br>while other users will simply see "Absence" as the title.',
    default_ongoing: 'Ongoing absence',
    default_ongoing_explained: 'Enable to automatically mark the absence as ongoing when this reason is selected when creating an absence',
    can_be_requested: 'Can be requested',
    can_be_requested_explained: 'Employees can select this reason when requesting absence'
  }
}
import DModal from '../common/DModal.vue'
import AppToggle from '../common/AppToggle.vue'
import DSelect from '../DSelect/DSelect.vue'
export default {
  components: { DModal, AppToggle, DSelect },
  props: {
    initTitle: {
      type: String,
      default: ''
    },
    absenceCodes: {
      type: Array,
      default: () => []
    },
    sdAbsenceCodes: {
      type: Array,
      default: () => []
    },
    isAbsenceIntegrationActive: {
      type: Boolean,
      default: false
    },
    salarySystem: {
      type: String,
      default: 'SD'
    }
  },
  directives: {
    focus: {
      inserted (el) {
        setTimeout(function () {
          el.focus()
        }, 150)
      }
    }
  },
  data () {
    return {
      newReasonTitle: '',
      errors: {},
      isCreating: false,
      defaultPrivate: true,
      defaultOngoing: false,
      canRequest: true,
      selectedAbsenceCodeId: null,
      selectedSdAbsenceCodeId: null,
      resetDSelectCounter: 0
    }
  },
  computed: {
    locale () {
      return this.$store.getters.locale
    },
    i18n () {
      return I18N[this.locale]
    },
    isSalarySystemSD () {
      return this.salarySystem === 'SD'
    },
    isSalarySystemKMD () {
      return this.salarySystem !== 'SD'
    },
    absenceCodesForDSelect () {
      if (!this.absenceCodes || !Array.isArray(this.absenceCodes)) {
        return []
      }
      return this.absenceCodes.map(code => {
        const absenceType = code.absence_type || ''
        const absenceTypeText = code.absence_type_text || ''
        return {
          id: code.id || '',
          name: absenceType + (absenceTypeText ? ' - ' + absenceTypeText : ''),
          value: code.id,
          original: code
        }
      })
    },
    selectedAbsenceCodeForDSelect () {
      if (!this.selectedAbsenceCodeId) {
        return []
      }
      const found = this.absenceCodesForDSelect.find(item => {
        return item.value === this.selectedAbsenceCodeId
      })
      return found ? [found] : []
    },
    sdAbsenceCodesForDSelect () {
      if (!this.sdAbsenceCodes || !Array.isArray(this.sdAbsenceCodes)) {
        return []
      }
      return this.sdAbsenceCodes.map(code => {
        const loenart = code.loenart ? String(code.loenart) : ''
        const title = code.title || ''
        return {
          id: code.id || '',
          name: (loenart ? loenart + ' - ' : '') + title,
          value: code.id,
          original: code
        }
      })
    },
    selectedSdAbsenceCodeForDSelect () {
      if (!this.selectedSdAbsenceCodeId) {
        return []
      }
      const found = this.sdAbsenceCodesForDSelect.find(item => {
        return item.value === this.selectedSdAbsenceCodeId
      })
      return found ? [found] : []
    },
    shouldShowDefaultOngoing () {
      // Hvis integrationen ikke er aktiv, vis altid toggle
      if (!this.isAbsenceIntegrationActive) {
        return true
      }
      // For SD system, vis altid toggle (da SD codes ikke har allow_open)
      if (this.isSalarySystemSD) {
        return true
      }
      // Hvis integrationen er aktiv men ingen årsag er valgt, vis toggle
      if (!this.selectedAbsenceCodeId) {
        return true
      }
      // Hvis integrationen er aktiv og en årsag er valgt, vis kun hvis allow_open er true
      const selectedCode = this.absenceCodesForDSelect.find(item => {
        return item.value === this.selectedAbsenceCodeId
      })
      if (selectedCode && selectedCode.original) {
        return selectedCode.original.allow_open === true
      }
      // Fallback: vis toggle hvis vi ikke kan finde koden
      return true
    }
  },
  methods: {
    onCancel () {
      this.$emit('cancel')
      this.reset()
    },
    onCreate () {
      if (this.newReasonTitle.length < 1) {
        this.$set(this.errors, 'newReasonTitle', this.i18n.need_a_title)
        return
      }
      const titleAndDefaultPrivate = {
        title: this.newReasonTitle,
        defaultPrivate: this.defaultPrivate,
        defaultOngoing: this.defaultOngoing,
        canRequest: this.canRequest,
        kmdAbsenceCodeId: this.isSalarySystemKMD ? this.selectedAbsenceCodeId : null,
        sdAbsenceCodeId: this.isSalarySystemSD ? this.selectedSdAbsenceCodeId : null
      }
      this.isCreating = true
      this.$emit('create', titleAndDefaultPrivate)
    },
    clearErrors () {
      this.errors = {}
    },
    onSelectAbsenceCode (item) {
      if (!item) {
        this.selectedAbsenceCodeId = null
        this.newReasonTitle = ''
        // Nulstil defaultOngoing når årsag fjernes
        this.defaultOngoing = false
      } else {
        this.selectedAbsenceCodeId = item.value
        // Sæt titlen automatisk til fraværskodens tekst
        if (item.original && item.original.absence_type_text) {
          this.newReasonTitle = item.original.absence_type_text
        } else if (item.name) {
          // Fallback til item.name hvis absence_type_text ikke findes
          const parts = item.name.split(' - ')
          this.newReasonTitle = parts.length > 1 ? parts[1] : parts[0]
        }
        // Hvis OPUS integrationen er aktiv og allow_open er true, slå default_ongoing automatisk til
        if (this.isAbsenceIntegrationActive && item.original && item.original.allow_open === true) {
          this.defaultOngoing = true
        } else if (this.isAbsenceIntegrationActive && item.original && item.original.allow_open === false) {
          // Hvis allow_open er false, nulstil defaultOngoing
          this.defaultOngoing = false
        }
      }
    },
    onSelectSdAbsenceCode (item) {
      if (!item) {
        this.selectedSdAbsenceCodeId = null
        this.newReasonTitle = ''
      } else {
        this.selectedSdAbsenceCodeId = item.value
        // Sæt titlen automatisk til fraværskodens tekst
        if (item.original && item.original.title) {
          this.newReasonTitle = item.original.title
        } else if (item.name) {
          // Fallback til item.name hvis title ikke findes
          const parts = item.name.split(' - ')
          this.newReasonTitle = parts.length > 1 ? parts[1] : parts[0]
        }
        // SD codes har ikke allow_open property, så vi ignorerer defaultOngoing logik
      }
    },
    reset () {
      this.newReasonTitle = ''
      this.errors = {}
      this.isCreating = false
      this.defaultPrivate = true
      this.defaultOngoing = false
      this.canRequest = true
      this.selectedAbsenceCodeId = null
      this.selectedSdAbsenceCodeId = null
      this.resetDSelectCounter = this.resetDSelectCounter + 1
    }
  },
  mounted () {
    if (this.initTitle) {
      this.newReasonTitle = this.initTitle
    }
  }
}
</script>

<style scoped>
.do-modal__body {
  background: #edf0f5;
  max-height: calc(100vh - 105px);
  overflow-y: auto;
  overscroll-behavior: contain;
  min-width: 500px;
  padding: 2rem;
}

.do-modal__footer {
  gap: 1rem;
}


.d-label-and-toggle {
  display: flex;
  align-items: center;
  justify-content: space-between;
  border: 1px solid #ccc;
  padding: 0.75rem;
  border-radius: 4px;
  cursor: pointer;
}

.d-label-and-toggle label {
  cursor: pointer;
}

.d-label-and-toggle:hover {
  background: aliceblue;
}

.d-group .d-input {
  height: 38px;
  margin-top: 0.25rem;
}

.marg-05-top {
  margin-top: 0.5rem;
}
.marg-025-bottom {
  margin-bottom: 0.25rem;
}
</style>



// WEBPACK FOOTER //
// AbsenceReasonsCreate.vue?252f2c05